import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { NotificationService } from '../common/service/notification.service';
import { ReportArchive } from '../model';
import { SpeciesNamesComponent } from '../common/species-names.component';
import { reportArchiveSiteData, SiteDataBadgeComponent } from '../common/geo/site-data-badge.component';

export type CardAction = 'add' | 'remove' | 'none';
export type CardVariant = 'default' | 'selected' | 'search' | 'compact';

@Component({
  selector: 'app-report-overview-card[report]',
  templateUrl: './report-overview-card.component.html',
  imports: [SpeciesNamesComponent, SiteDataBadgeComponent, CommonModule, MatButtonModule, MatIconModule, TranslatePipe],
  styleUrl: './report-overview-card.component.scss',
})
export class ReportOverviewCardComponent {
  private readonly toastr = inject(NotificationService);

  @Input() report!: ReportArchive;
  @Input() reportId?: string;
  @Input() action: CardAction = 'none';
  @Input() disabled = false;
  @Input() variant: CardVariant = 'default';
  @Input() cssClass = '';
  @Input() articleNumbers?: string[];
  @Input() showPreview = false;
  @Input() showDownload = false;
  @Output() actionClick = new EventEmitter<void>();
  @Output() previewClick = new EventEmitter<void>();
  @Output() downloadClick = new EventEmitter<void>();

  // The same fold the archive list and detail use — frozen tiers only, never the live site.
  protected get siteData() {
    return reportArchiveSiteData(this.report?.degrees);
  }

  onActionClick(event: Event): void {
    event.stopPropagation();
    this.actionClick.emit();
  }

  onPreviewClick(event: Event): void {
    event.stopPropagation();
    this.previewClick.emit();
  }

  onDownloadClick(event: Event): void {
    event.stopPropagation();
    this.downloadClick.emit();
  }

  async copyIdToClipboard(event: Event): Promise<void> {
    event.stopPropagation();
    const id = this.report?.id || this.reportId;
    if (!id) return;

    try {
      await navigator.clipboard.writeText(id);
      this.toastr.success('Id_copied_to_clipboard_tr');
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      this.toastr.error('failed_to_copy_to_clipboard_tr');
    }
  }

  get actionIcon(): string {
    return this.action === 'add' ? 'add' : 'close';
  }

  get actionColor(): 'primary' | 'warn' {
    return this.action === 'add' ? 'primary' : 'warn';
  }

  get actionTitle(): string {
    return this.action === 'add' ? 'add_tr' : 'Remove_tr';
  }
}
